import type { JSX } from 'react';

import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import { useTranslation } from 'react-i18next';

import type { JizdaListItem } from '@/api/types/Jizda';
import { useAppSelector } from '@/store/hooks';
import { selectMySub } from '@/store/userSlice';
import { selectAssignedUserName } from '@/store/usersSlice';
import { formatTimeCs } from '@/utils/date';

import { resolveCalendarRowStyle } from './rowStyle';

export interface CalendarJizdaRowProps {
  item: JizdaListItem;
  onOpen: (id: string) => void;
}

// One fůra in a day column (CHR-03 §6 / ADR-0051). Renders, top-to-bottom on
// one compact pill:
//   - TOP: (optional) plannedArrival time prefix (primarily for brambory) +
//     route `loadingPlace → unloadingPlace` + a note tooltip icon (top-right)
//     when `noteForDispatcherSnippet` exists.
//   - BOTTOM (pinned, space-between): dispatcher NAME when assigned (via the
//     chained resolver) — or "nepřiřazeno" — LEFT, vehicle SPZ RIGHT.
// Colour is semantic (theme.statusPalette): assigned = green (done),
// unassigned = red (paused); STORNO = red + strikethrough (wins over
// assignment); deleted = faded. Click → detail (host navigates).
export const CalendarJizdaRow = ({
  item,
  onOpen,
}: CalendarJizdaRowProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('orders');
  const dispatcherName = useAppSelector(selectAssignedUserName(item.assignedDispatcherSub));
  const mySub = useAppSelector(selectMySub);

  const style = resolveCalendarRowStyle(item, mySub);
  // tone → theme.statusPalette: assigned=green, mine=yellow, unassigned=red.
  const palette =
    style.tone === 'assigned'
      ? theme.statusPalette.done
      : style.tone === 'mine'
        ? theme.statusPalette.mine
        : theme.statusPalette.paused;
  const time = formatTimeCs(item.plannedArrival);
  const note = item.noteForDispatcherSnippet;
  const spz = item.vehicle?.spz ?? null;
  const route =
    `${item.loadingPlace ?? ''} ${t('calendar.routeSeparator')} ${item.unloadingPlace ?? ''}`.trim();

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(item.id);
        }
      }}
      aria-label={t('calendar.openAria')}
      data-testid={`calendar-jizda-${item.id}`}
      data-tone={style.tone}
      data-storno={style.strikeThrough ? 'true' : 'false'}
      data-deleted={style.faded ? 'true' : 'false'}
      sx={{
        cursor: 'pointer',
        borderRadius: 1.5,
        px: theme.spacing(1),
        py: theme.spacing(0.75),
        // Semantic status tint (theme.statusPalette — no raw hex): soft bg +
        // a left accent bar in the status colour, mirroring the overview's
        // status-pill language.
        bgcolor: palette.bg,
        color: palette.color,
        borderLeft: `3px solid ${palette.color}`,
        opacity: style.faded ? 0.5 : 1,
        textDecoration: style.strikeThrough ? 'line-through' : 'none',
        // Tap target ≥44px on touch — the pill is naturally ~40px, floor it.
        minHeight: 44,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.25),
        transition: theme.transitions.create(['box-shadow', 'filter']),
        '&:hover': {
          boxShadow: theme.elevation.shadow1,
          filter: 'brightness(0.97)',
        },
        '&:focus-visible': {
          outline: `2px solid ${palette.color}`,
          outlineOffset: 1,
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" gap={theme.spacing(0.75)} sx={{ minWidth: 0 }}>
        {time ? (
          <Typography
            component="span"
            sx={{
              fontFamily: theme.monospaceFontFamily,
              fontFeatureSettings: '"tnum"',
              fontSize: 11.5,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {time}
          </Typography>
        ) : null}
        <Typography
          component="span"
          sx={{
            fontSize: 12.5,
            fontWeight: 500,
            // Wrap cell text to multiple lines instead of clipping/ellipsis
            // (change-spec item 5) — long places/routes stay fully readable.
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          {route || t('calendar.routeSeparator')}
        </Typography>
        {note ? (
          <Tooltip title={note} placement="top">
            <StickyNote2OutlinedIcon
              sx={{ fontSize: 14, flexShrink: 0 }}
              data-testid={`calendar-note-${item.id}`}
              aria-label={t('calendar.noteTooltipFallback')}
            />
          </Tooltip>
        ) : null}
      </Stack>
      {/* Cell bottom row (change-spec item 1): a single space-between line
          pinned to the bottom of the flex column (`mt:auto`) — dispatcher NAME
          LEFT, SPZ RIGHT. Both small/secondary, `minWidth:0` + wrap-safe so a
          long name and a SPZ never collide. Unassigned keeps the existing red
          treatment (the cell's `paused` palette colour) still bottom-left. */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        gap={theme.spacing(0.75)}
        sx={{ mt: 'auto', pt: theme.spacing(0.25), minWidth: 0 }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: 11,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            opacity: 0.85,
            minWidth: 0,
          }}
          data-testid={`calendar-dispatcher-${item.id}`}
        >
          {item.assignedDispatcherSub !== null
            ? dispatcherName
            : t('calendar.unassigned')}
        </Typography>
        {spz ? (
          <Typography
            component="span"
            sx={{
              fontFamily: theme.monospaceFontFamily,
              fontFeatureSettings: '"tnum"',
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: '0.02em',
              opacity: 0.7,
              flexShrink: 0,
            }}
            data-testid={`calendar-spz-${item.id}`}
          >
            {spz}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
};
